import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Collapse, IconButton } from '@mui/material';
import { Close, NotificationsActiveOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const SNOOZE_STORAGE_KEY = 'pushPermissionBannerSnoozedUntil';
const SNOOZE_DAYS = 3;

const isSnoozed = () => {
  const until = localStorage.getItem(SNOOZE_STORAGE_KEY);
  return until !== null && Date.now() < Number(until);
};

interface NotificationPermissionBannerProps {
  visible: boolean;
  onEnable: () => Promise<boolean>;
}

export const NotificationPermissionBanner: React.FC<
  NotificationPermissionBannerProps
> = ({ visible, onEnable }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(() => !isSnoozed());
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (visible && !isSnoozed()) setOpen(true);
  }, [visible]);

  const handleEnable = async () => {
    setBusy(true);
    setFailed(false);
    try {
      if (await onEnable()) setOpen(false);
      else setFailed(true);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  const handleDismiss = () => {
    setOpen(false);
    localStorage.setItem(
      SNOOZE_STORAGE_KEY,
      String(Date.now() + SNOOZE_DAYS * 24 * 60 * 60 * 1000)
    );
  };

  if (!visible) {
    return null;
  }

  return (
    <Collapse in={open}>
      <Alert
        severity={failed ? 'error' : 'info'}
        icon={false}
        action={
          <Box display="flex" alignItems="center" gap={1}>
            <Button
              size="small"
              color="inherit"
              disabled={busy}
              onClick={handleEnable}
              sx={{ whiteSpace: 'nowrap', fontWeight: 700 }}
            >
              {t('notification.permission.enable')}
            </Button>
            <IconButton
              size="small"
              color="inherit"
              onClick={handleDismiss}
              aria-label={t('notification.permission.dismiss')}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        }
        sx={{
          borderRadius: 0,
          py: 0.5,
          '& .MuiAlert-message': {
            py: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          },
          '& .MuiAlert-action': {
            py: 0,
            pl: 2,
            ml: 'auto'
          }
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <NotificationsActiveOutlined fontSize="small" />
          {t(
            failed
              ? 'notification.permission.error'
              : 'notification.permission.bannerMessage'
          )}
        </Box>
      </Alert>
    </Collapse>
  );
};
